import React, { useEffect, useRef } from 'react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import s from './StaffContextMenu.module.css';

/**
 * AB#1752 — Staff-first quick-book entry-point (mockup §D).
 *
 * Right-click on a staff column header opens this context menu. The
 * primary action is "Book with {name}…" which fires `onBookWith` and
 * launches the `QuickBookModal` with `lockedStaffId` / `lockedStaffName`
 * set so the slot search is constrained to that staff.
 *
 * The "View schedule" and "Manage staff" rows are placeholders for the
 * existing per-staff schedule editor and ManageStaffModal — those wire
 * up in a follow-up PBI; for now they no-op so the menu still reads as
 * the full mockup §D shape but only the booking entry-point actually
 * acts.
 */
export interface StaffContextMenuProps {
    /** Viewport coords (clientX/clientY from the right-click event). */
    x: number;
    y: number;
    staffId: number;
    staffName: string;
    onBookWith: () => void;
    /** AB#1976 — open the quick add-break / time-off editor for this staff. */
    onAddBreak: () => void;
    /** AB#2019 — open the read-only availability status panel for this staff. */
    onViewStatus: () => void;
    onClose: () => void;
}

const StaffContextMenu: React.FC<StaffContextMenuProps> = ({
    x, y, staffName, onBookWith, onAddBreak, onViewStatus, onClose,
}) => {
    const { t } = useLanguage();
    const ref = useRef<HTMLDivElement | null>(null);

    // Close on click-outside, Escape, or window scroll/resize.
    useEffect(() => {
        const onDocClick = (e: MouseEvent) => {
            if (!ref.current) return;
            if (e.target instanceof Node && !ref.current.contains(e.target)) {
                onClose();
            }
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        // Use capture so we catch the click even if a child swallowed it.
        document.addEventListener('mousedown', onDocClick, true);
        document.addEventListener('keydown', onKey);
        window.addEventListener('scroll', onClose, true);
        window.addEventListener('resize', onClose);
        return () => {
            document.removeEventListener('mousedown', onDocClick, true);
            document.removeEventListener('keydown', onKey);
            window.removeEventListener('scroll', onClose, true);
            window.removeEventListener('resize', onClose);
        };
    }, [onClose]);

    // Clamp to viewport so the menu doesn't overflow at the bottom-right.
    const style: React.CSSProperties = {
        top: Math.min(y, window.innerHeight - 160),
        left: Math.min(x, window.innerWidth - 240),
    };

    return (
        <div
            ref={ref}
            className={s.menu}
            style={style}
            role="menu"
            aria-label={t('calendar.staffMenu.aria').replace('{name}', staffName)}
        >
            <button
                type="button"
                role="menuitem"
                className={s.row}
                onClick={() => {
                    onBookWith();
                    onClose();
                }}
            >
                <span className={s.glyph} aria-hidden="true">+</span>
                {t('calendar.staffMenu.bookWith').replace('{name}', staffName)}
            </button>
            <button
                type="button"
                role="menuitem"
                className={s.row}
                onClick={() => {
                    onAddBreak();
                    onClose();
                }}
            >
                <span className={s.glyph} aria-hidden="true">☕</span>
                {t('calendar.staffMenu.addBreak')}
            </button>
            <button
                type="button"
                role="menuitem"
                className={s.row}
                onClick={() => {
                    onViewStatus();
                    onClose();
                }}
            >
                <span className={s.glyph} aria-hidden="true">👁</span>
                {t('calendar.staffMenu.viewStatus')}
            </button>
            <button
                type="button"
                role="menuitem"
                className={`${s.row} ${s.rowMuted}`}
                disabled
                title={t('common.comingSoon')}
            >
                <span className={s.glyph} aria-hidden="true">📅</span>
                {t('calendar.staffMenu.viewSchedule').replace('{name}', staffName)}
            </button>
            <button
                type="button"
                role="menuitem"
                className={`${s.row} ${s.rowMuted}`}
                disabled
                title={t('common.comingSoon')}
            >
                <span className={s.glyph} aria-hidden="true">⚙</span>
                {t('calendar.staffMenu.manage').replace('{name}', staffName)}
            </button>
        </div>
    );
};

export default StaffContextMenu;
